'use client';

import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface CartItemData {
  id: string;
  name: string;
  image: string;
  alt: string;
  weight: string;
  pricePerKg: number;
  quantity: number;
  totalPrice: number;
  inStock: boolean;
  priceChange?: {
    type: 'increase' | 'decrease';
    percentage: number;
  };
}

interface CartItemProps {
  item: CartItemData;
  onQuantityChange: (id: string, quantity: number) => void;
  onWeightChange: (id: string, weight: string) => void;
  onRemove: (id: string) => void;
  onSaveForLater: (id: string) => void;
}

const CartItem = ({ item, onQuantityChange, onWeightChange, onRemove, onSaveForLater }: CartItemProps) => {
  const [selectedWeight, setSelectedWeight] = useState(item.weight);
  const weightOptions = ['250g', '500g', '750g', '1kg', '2kg'];

  const handleWeightChange = (weight: string) => {
    setSelectedWeight(weight);
    onWeightChange(item.id, weight);
  };

  const handleQuantityChange = (delta: number) => {
    const newQuantity = Math.max(1, item.quantity + delta);
    onQuantityChange(item.id, newQuantity);
  };

  return (
    <div className="bg-card border border-border rounded-md p-4 transition-smooth hover:shadow-elevation-md">
      <div className="flex gap-4">
        {/* Product Image */}
        <div className="relative w-24 h-24 flex-shrink-0 overflow-hidden rounded-md bg-muted">
          <AppImage
            src={item.image}
            alt={item.alt}
            className="w-full h-full object-cover"
          />
          {!item.inStock && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
              <span className="text-white text-xs font-medium">Out of Stock</span>
            </div>
          )}
        </div>

        {/* Product Details */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex-1">
              <h3 className="font-heading font-semibold text-base text-foreground mb-1">
                {item.name}
              </h3>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-data text-sm text-muted-foreground">
                  ₹{item.pricePerKg.toFixed(2)}/kg
                </span>
                {item.priceChange && (
                  <div className={`flex items-center gap-1 text-xs ${
                    item.priceChange.type === 'increase' ? 'text-error' : 'text-success'
                  }`}>
                    <Icon 
                      name={item.priceChange.type === 'increase' ? 'ArrowTrendingUpIcon' : 'ArrowTrendingDownIcon'} 
                      size={12} 
                    />
                    <span>{item.priceChange.percentage}%</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={() => onRemove(item.id)}
              className="p-1 text-muted-foreground hover:text-error transition-smooth"
              aria-label="Remove item"
            >
              <Icon name="TrashIcon" size={18} />
            </button>
          </div>

          {/* Weight Selector */}
          <div className="mb-3">
            <label className="font-caption text-xs text-muted-foreground mb-1 block">
              Select Weight
            </label>
            <div className="flex gap-2 flex-wrap">
              {weightOptions.map((weight) => (
                <button
                  key={weight}
                  onClick={() => handleWeightChange(weight)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-smooth ${
                    selectedWeight === weight
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-foreground hover:bg-border'
                  }`}
                >
                  {weight}
                </button>
              ))}
            </div>
          </div>

          {/* Quantity Controls & Price */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="font-caption text-xs text-muted-foreground">Quantity:</span>
              <div className="flex items-center gap-2 bg-muted rounded-md">
                <button
                  onClick={() => handleQuantityChange(-1)}
                  disabled={item.quantity <= 1}
                  className="p-2 hover:bg-border transition-smooth disabled:opacity-50 disabled:cursor-not-allowed rounded-l-md"
                  aria-label="Decrease quantity"
                >
                  <Icon name="MinusIcon" size={16} />
                </button>
                <span className="font-data font-medium text-sm w-8 text-center">
                  {item.quantity}
                </span>
                <button
                  onClick={() => handleQuantityChange(1)}
                  className="p-2 hover:bg-border transition-smooth rounded-r-md"
                  aria-label="Increase quantity"
                >
                  <Icon name="PlusIcon" size={16} />
                </button>
              </div>
            </div>

            <div className="text-right">
              <div className="font-heading font-bold text-lg text-primary">
                ₹{item.totalPrice.toFixed(2)}
              </div>
            </div>
          </div>

          {/* Save for Later */}
          <button
            onClick={() => onSaveForLater(item.id)}
            className="mt-3 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-smooth"
          >
            <Icon name="BookmarkIcon" size={16} />
            <span>Save for later</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartItem;